import { format, parseISO, addDays } from "date-fns";
import { TZDate } from "@date-fns/tz";

const APP_TZ = "America/Vancouver";

const pad = (n: number) => String(n).padStart(2, "0");

/** Format an ISO date string as a readable date+time in the app timezone */
export function formatDateTime(iso: string): string {
  try {
    const date = new TZDate(parseISO(iso), APP_TZ);
    return format(date, "MMM d, yyyy h:mm a");
  } catch {
    return iso;
  }
}

/** Same as formatDateTime, but with the day of week prefixed */
export function formatDateTimeWithDay(iso: string): string {
  try {
    const date = new TZDate(parseISO(iso), APP_TZ);
    return format(date, "EEE, MMM d, yyyy h:mm a");
  } catch {
    return iso;
  }
}

/** Same as formatDateTime, but omits the year when it matches the current year */
export function formatDateTimeSmart(iso: string): string {
  try {
    const date = new TZDate(parseISO(iso), APP_TZ);
    const isCurrentYear = date.getFullYear() === new TZDate(new Date(), APP_TZ).getFullYear();
    return format(date, isCurrentYear ? "MMM d, h:mm a" : "MMM d, yyyy h:mm a");
  } catch {
    return iso;
  }
}

/** Format an ISO date string as a readable date only in the app timezone */
export function formatDate(iso: string): string {
  try {
    const date = new TZDate(parseISO(iso), APP_TZ);
    return format(date, "MMM d, yyyy");
  } catch {
    return iso;
  }
}

/** Convert an ISO string to a datetime-local input value ("yyyy-MM-dd'T'HH:mm") */
export function toLocalDatetimeValue(iso: string): string {
  try {
    const date = new TZDate(parseISO(iso), APP_TZ);
    return format(date, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return "";
  }
}

/**
 * Convert a datetime-local input value ("yyyy-MM-dd'T'HH:mm") back to an ISO
 * string, treating the value as Vancouver time.
 * Manually parses components to avoid new Date() interpreting the string as UTC.
 */
export function localDatetimeToIso(localValue: string): string {
  if (!localValue) return "";
  const [datePart, timePart] = localValue.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  return new TZDate(y, mo - 1, d, h, mi, APP_TZ).toISOString();
}

/** Return a default event start time: tomorrow at 9 PM Vancouver time */
export function defaultStartTime(): string {
  const today = new TZDate(new Date(), APP_TZ);
  const tomorrow = addDays(today, 1);
  return `${format(tomorrow, "yyyy-MM-dd")}T21:00`;
}

/**
 * Return a default event end time: 2 hours after start.
 * Uses string arithmetic to avoid timezone/Date mutation pitfalls.
 */
export function defaultEndTime(start: string): string {
  try {
    const [date, time] = start.split("T");
    const [h, m] = time.split(":").map(Number);
    const newH = h + 2;
    if (newH < 24) return `${date}T${pad(newH)}:${pad(m)}`;
    // Crosses midnight — roll over to the next day
    const [y, mo, day] = date.split("-").map(Number);
    return `${format(addDays(new Date(y, mo - 1, day), 1), "yyyy-MM-dd")}T${pad(newH - 24)}:${pad(m)}`;
  } catch {
    return "";
  }
}
